import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { ProductBrowseStatusCounts } from "../../services/ProductsService";
import {
  DISCOUNT_OPTIONS,
  FEED_SORT_LABELS,
  PRICE_CAPS,
  STATUS_LABELS,
  cleanPrice,
  hasStatusSignal,
  statusCountFor,
  type FeedFilterHandlers,
  type FeedFilterValues,
  type FeedSort,
  type FeedStatus,
} from "./feedFilterLogic";
import { trapDialogFocus } from "../guest-deals/GuestDealsUi";
import { categoryTitle } from "./DealRow";
import { HF } from "./homeFeedUi";

/**
 * Grid-mode filters for the logged-out feed: a sticky sidebar on desktop and a
 * bottom sheet on phones/tablets. Every option maps 1:1 onto a field the
 * public browse endpoint honours (sort, categories, status, minDiscount,
 * min/maxStorePrice).
 */

// ---------- pieces ----------

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="border-t py-3.5 first:border-t-0 first:pt-1" style={{ borderColor: HF.divider }}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.07em]" style={{ color: HF.text3 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function OptionRow({
  name,
  checked,
  label,
  count,
  onSelect,
}: {
  name: string;
  checked: boolean;
  label: string;
  count?: number;
  onSelect: () => void;
}) {
  return (
    <label
      className="-mx-2 flex min-h-[34px] cursor-pointer items-center gap-2.5 rounded-[8px] px-2 text-[14px] transition-colors hover:bg-[var(--hf-hover)] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#C4B5FD]"
      style={{ color: checked ? HF.ink : HF.text2, fontWeight: checked ? 650 : 500 }}
    >
      <input type="radio" name={name} checked={checked} onChange={onSelect} className="sr-only" />
      <span
        aria-hidden="true"
        className="h-4 w-4 flex-shrink-0 rounded-full bg-[var(--hf-surface)] transition-[border] duration-150"
        style={{ border: checked ? `5px solid ${HF.brandText}` : `1.5px solid ${HF.chipBorder}` }}
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="flex-shrink-0 text-[12.5px] font-medium tabular-nums" style={{ color: HF.muted }}>
          {count.toLocaleString()}
        </span>
      )}
    </label>
  );
}

function PillOption({ name, checked, label, onSelect }: { name: string; checked: boolean; label: string; onSelect: () => void }) {
  return (
    <label
      className="flex h-8 cursor-pointer items-center justify-center rounded-full border px-3 text-[13px] transition-colors hover:border-[var(--hf-violet-border)] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#C4B5FD]"
      style={
        checked
          ? { borderColor: HF.brand, background: HF.brand, color: "#FFFFFF", fontWeight: 700 }
          : { borderColor: HF.chipBorder, background: HF.surface, color: HF.text2, fontWeight: 600 }
      }
    >
      <input type="radio" name={name} checked={checked} onChange={onSelect} className="sr-only" />
      {label}
    </label>
  );
}

function PriceInputs({ min, max, onPrice, idPrefix }: { min: string; max: string; onPrice: (min: string, max: string) => void; idPrefix: string }) {
  const [minInput, setMinInput] = useState(min);
  const [maxInput, setMaxInput] = useState(max);
  const committed = useRef({ min, max });

  // Chips, "Clear all" and URL changes win over whatever was typed.
  useEffect(() => {
    committed.current = { min, max };
    setMinInput(min);
    setMaxInput(max);
  }, [min, max]);

  useEffect(() => {
    const nextMin = cleanPrice(minInput);
    const nextMax = cleanPrice(maxInput);
    if (nextMin === committed.current.min && nextMax === committed.current.max) return;
    const timer = window.setTimeout(() => {
      committed.current = { min: nextMin, max: nextMax };
      onPrice(nextMin, nextMax);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [minInput, maxInput, onPrice]);

  const field = (id: string, label: string, value: string, set: (value: string) => void, placeholder: string) => (
    <label
      htmlFor={id}
      className="flex h-9 min-w-0 flex-1 items-center gap-1 rounded-[10px] border bg-[var(--hf-surface)] px-2.5 text-[14px] transition focus-within:border-[#A78BFA] focus-within:ring-2 focus-within:ring-[var(--hf-violet-soft)]"
      style={{ borderColor: HF.chipBorder, color: HF.muted }}
    >
      <span aria-hidden="true">$</span>
      <input
        id={id}
        aria-label={label}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(event) => set(event.target.value.replace(/[^\d.]/g, "").slice(0, 7))}
        className="w-full min-w-0 border-0 bg-transparent font-semibold tabular-nums outline-none placeholder:font-medium placeholder:text-[var(--hf-placeholder)]"
        style={{ color: HF.ink }}
      />
    </label>
  );

  return (
    <div className="flex items-center gap-2">
      {field(`${idPrefix}-min-price`, "Minimum price", minInput, setMinInput, "Min")}
      <span className="text-[13px]" style={{ color: HF.muted }} aria-hidden="true">–</span>
      {field(`${idPrefix}-max-price`, "Maximum price", maxInput, setMaxInput, "Max")}
    </div>
  );
}

/** Every filter section; shared by the desktop sidebar and the mobile sheet. */
export function FeedFilterPanel({
  values,
  handlers,
  categories,
  statusCounts,
  sortLast = false,
  rail = false,
}: {
  values: FeedFilterValues;
  handlers: FeedFilterHandlers;
  categories: Array<{ id: string; count: number }>;
  statusCounts?: ProductBrowseStatusCounts;
  /** The sheet leads with filters; its trigger already sits next to a sort control. */
  sortLast?: boolean;
  /** Desktop store rail: departments as an aisle list, deal type left to the chips above the grid. */
  rail?: boolean;
}) {
  const idPrefix = useId().replace(/:/g, "");
  const [expanded, setExpanded] = useState(false);
  const ranked = [...categories].filter((item) => item.id).sort((a, b) => b.count - a.count);
  const TOP = rail ? 6 : 8;
  let visible = expanded ? ranked : ranked.slice(0, TOP);
  if (!expanded && values.category && !visible.some((item) => item.id === values.category)) {
    const current = ranked.find((item) => item.id === values.category);
    visible = [...visible, current || { id: values.category, count: 0 }];
  }
  const capActive = (cap: number) => !values.minPrice && values.maxPrice === String(cap);

  const sortSection = (
    <Section title="Sort by">
      <div role="radiogroup" aria-label="Sort by">
        {(Object.keys(FEED_SORT_LABELS) as FeedSort[]).map((value) => (
          <OptionRow
            key={value}
            name={`${idPrefix}-sort`}
            checked={values.sort === value}
            label={FEED_SORT_LABELS[value]}
            onSelect={() => handlers.onSort(value)}
          />
        ))}
      </div>
    </Section>
  );

  return (
    <div>
      {!sortLast && !rail && sortSection}
      {rail && ranked.length > 0 && (
        <nav aria-label="Departments" className="flex flex-col pb-3">
          <span className="px-1 pb-1.5 text-[15px] font-extrabold" style={{ color: HF.ink }}>Aisles</span>
          {[{ id: "", count: 0 }, ...visible].map((item) => {
            const selected = (values.category || "") === item.id;
            return (
              <button
                key={item.id || "all"}
                type="button"
                aria-pressed={selected}
                onClick={() => handlers.onCategory(item.id)}
                className="flex items-center justify-between rounded-[10px] px-3 py-[7px] text-left text-[14.5px] transition-colors hover:bg-[var(--hf-hover-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
                style={selected ? { background: HF.inkBtn, color: HF.onInkBtn, fontWeight: 700 } : { color: HF.ink, fontWeight: 600 }}
              >
                <span className="truncate">{item.id ? categoryTitle(item.id) : "All deals"}</span>
                {item.count > 0 && <span className="ml-2 flex-shrink-0 text-[13px] font-medium" style={{ color: selected ? undefined : HF.muted, opacity: selected ? 0.7 : 1 }}>{item.count.toLocaleString()}</span>}
              </button>
            );
          })}
          {ranked.length > TOP && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              className="mt-1 self-start px-3 text-[13.5px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
              style={{ color: HF.store }}
            >
              {expanded ? "Show less" : `Show all ${ranked.length}`}
            </button>
          )}
        </nav>
      )}
      {rail && (
        <Section title="Sort by">
          <label className="relative flex h-10 items-center rounded-[10px] border px-3 focus-within:border-[#A78BFA] focus-within:ring-2 focus-within:ring-[var(--hf-violet-soft)]" style={{ borderColor: HF.chipBorder }}>
            <select
              aria-label="Sort by"
              value={values.sort}
              onChange={(event) => handlers.onSort(event.target.value as FeedSort)}
              className="w-full cursor-pointer appearance-none bg-transparent pr-6 text-[14px] font-semibold outline-none"
              style={{ color: HF.ink }}
            >
              {(Object.keys(FEED_SORT_LABELS) as FeedSort[]).map((value) => (
                <option key={value} value={value}>{FEED_SORT_LABELS[value]}</option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-3" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: HF.text3 }}><path d="M6 9l6 6 6-6" /></svg>
          </label>
        </Section>
      )}
      {!rail && ranked.length > 0 && (
        <Section title="Category">
          <div role="radiogroup" aria-label="Category">
            <OptionRow name={`${idPrefix}-category`} checked={!values.category} label="All categories" onSelect={() => handlers.onCategory("")} />
            {visible.map((item) => (
              <OptionRow
                key={item.id}
                name={`${idPrefix}-category`}
                checked={values.category === item.id}
                label={categoryTitle(item.id)}
                count={item.count || undefined}
                onSelect={() => handlers.onCategory(item.id)}
              />
            ))}
          </div>
          {ranked.length > TOP && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              className="mt-1 flex items-center gap-1 rounded text-[13px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
              style={{ color: HF.store }}
            >
              {expanded ? "Show less" : `Show all ${ranked.length}`}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={expanded ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
              </svg>
            </button>
          )}
        </Section>
      )}

      <Section title="Discount">
        <div role="radiogroup" aria-label="Discount" className="grid grid-cols-4 gap-1.5">
          <PillOption name={`${idPrefix}-discount`} checked={!values.minDiscount} label="Any" onSelect={() => handlers.onDiscount("")} />
          {DISCOUNT_OPTIONS.map((value) => (
            <PillOption
              key={value}
              name={`${idPrefix}-discount`}
              checked={values.minDiscount === String(value)}
              label={`${value}%+`}
              onSelect={() => handlers.onDiscount(String(value))}
            />
          ))}
        </div>
      </Section>

      <Section title="Price">
        <PriceInputs min={values.minPrice} max={values.maxPrice} onPrice={handlers.onPrice} idPrefix={idPrefix} />
        <div className="mt-2.5 grid grid-cols-3 gap-1.5">
          {PRICE_CAPS.map((cap) => {
            const active = capActive(cap);
            return (
              <button
                key={cap}
                type="button"
                aria-pressed={active}
                onClick={() => handlers.onPrice("", active ? "" : String(cap))}
                className="flex h-7 items-center justify-center whitespace-nowrap rounded-full border px-1 text-[12.5px] transition-colors hover:border-[var(--hf-violet-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
                style={
                  active
                    ? { borderColor: HF.violetBorder, background: HF.violetSoft, color: HF.brandText, fontWeight: 700 }
                    : { borderColor: HF.chipBorder, background: HF.surface, color: HF.text2, fontWeight: 600 }
                }
              >
                Under ${cap}
              </button>
            );
          })}
        </div>
      </Section>

      {!rail && (hasStatusSignal(statusCounts) || values.status !== "all") && (
        <Section title="Show">
          <div role="radiogroup" aria-label="Deal type">
            {(Object.keys(STATUS_LABELS) as FeedStatus[]).map((value) => {
              const count = value === "all" ? undefined : statusCountFor(value, statusCounts);
              if (value !== "all" && value !== values.status && !count) return null;
              return (
                <OptionRow
                  key={value}
                  name={`${idPrefix}-status`}
                  checked={values.status === value}
                  label={STATUS_LABELS[value]}
                  count={count}
                  onSelect={() => handlers.onStatus(value)}
                />
              );
            })}
          </div>
        </Section>
      )}
      {sortLast && sortSection}
    </div>
  );
}

/** Desktop: sticky under the sticky header, scrolls on its own when taller than the viewport. */
export function FeedFilterSidebar({
  top,
  activeCount,
  onClearAll,
  storeLabel,
  footer,
  ...panel
}: Parameters<typeof FeedFilterPanel>[0] & {
  top: number;
  activeCount: number;
  onClearAll: () => void;
  /** Store rail header (the same rail as the store's sectioned page). */
  storeLabel?: string;
  footer?: ReactNode;
}) {
  return (
    <aside
      aria-label="Filters"
      data-testid="filter-sidebar"
      className="sticky hidden w-[232px] flex-shrink-0 self-start overflow-y-auto overscroll-contain border-r pb-6 pl-4 pr-3 pt-6 [scrollbar-width:thin] lg:block"
      style={{ top, height: `calc(100vh - ${top}px)`, borderColor: HF.border }}
    >
      {storeLabel ? (
        <div className="mb-3 flex items-center gap-2 border-b px-1 pb-3" style={{ borderColor: HF.border }}>
          <span className="min-w-0 flex-1 truncate text-[22px] font-extrabold leading-tight tracking-[-0.5px]" style={{ color: HF.ink }}>{storeLabel}</span>
          {activeCount > 0 && (
            <button type="button" onClick={onClearAll} className="flex-shrink-0 text-[13px] font-semibold hover:underline" style={{ color: HF.store }}>
              Clear all
            </button>
          )}
        </div>
      ) : (
        <div className="mb-1.5 flex h-9 items-center justify-between">
          <h2 className="text-[16px] font-bold tracking-[-0.2px]" style={{ color: HF.ink }}>Filters</h2>
          {activeCount > 0 && (
            <button type="button" onClick={onClearAll} className="text-[13px] font-semibold hover:underline" style={{ color: HF.store }}>
              Clear all
            </button>
          )}
        </div>
      )}
      <FeedFilterPanel {...panel} rail={Boolean(storeLabel)} />
      {footer && <div className="mt-3">{footer}</div>}
    </aside>
  );
}

export function FiltersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </svg>
  );
}

function CloseIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Phones and tablets: a bottom sheet; filters apply live, the footer shows the live total. */
export function FeedFilterSheet({
  total,
  loading,
  activeCount,
  onClearAll,
  onClose,
  ...panel
}: Parameters<typeof FeedFilterPanel>[0] & {
  total: number;
  loading: boolean;
  activeCount: number;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      trapDialogFocus(event, dialogRef.current);
    };
    window.addEventListener("keydown", handleKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyboard);
      previouslyFocused?.focus();
    };
  }, []);

  return createPortal(
    <div
      className="sheet-backdrop-in fixed inset-0 z-[10000] flex items-end justify-center lg:hidden"
      style={{ background: "var(--hf-overlay)", fontFamily: HF.font }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      data-testid="filter-sheet-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feed-filter-sheet-title"
        tabIndex={-1}
        className="sheet-panel-in flex max-h-[88dvh] w-full max-w-[560px] flex-col rounded-t-[22px] bg-[var(--hf-surface)] shadow-[0_-12px_40px_var(--hf-shadow)] outline-none"
        style={{ color: HF.ink }}
      >
        <div className="flex justify-center pb-1 pt-2.5" aria-hidden="true">
          <span className="h-1 w-10 rounded-full" style={{ background: HF.chipBorder }} />
        </div>
        <div className="flex items-center justify-between px-5 pb-2">
          <h2 id="feed-filter-sheet-title" className="text-[18px] font-bold tracking-[-0.3px]">
            Filters{activeCount > 0 && <span className="ml-1.5 text-[14px] font-semibold" style={{ color: HF.text3 }}>({activeCount})</span>}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="-mr-2 flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-[var(--hf-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
            style={{ color: HF.text2 }}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-2" style={{ borderTop: `1px solid ${HF.divider}` }}>
          <div className="pt-3">
            <FeedFilterPanel {...panel} sortLast />
          </div>
        </div>
        <div
          className="flex gap-2.5 px-4 pt-3"
          style={{ borderTop: `1px solid ${HF.divider}`, paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
        >
          <button
            type="button"
            onClick={onClearAll}
            disabled={activeCount === 0}
            className="h-12 flex-1 rounded-full border text-[15px] font-semibold transition disabled:opacity-40"
            style={{ borderColor: HF.chipBorder, color: HF.text2 }}
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-12 flex-[1.6] rounded-full text-[15px] font-bold text-white transition hover:opacity-95"
            style={{ background: HF.brand }}
          >
            {loading ? "Show deals" : `Show ${total.toLocaleString()} ${total === 1 ? "deal" : "deals"}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Removable chips for whatever is narrowing the grid. */
export function ActiveFilterChips({ chips, onClearAll }: { chips: Array<{ key: string; label: string; onRemove: () => void }>; onClearAll: () => void }) {
  if (chips.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 px-1 sm:mb-4" data-testid="active-filters">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          aria-label={`Remove filter: ${chip.label}`}
          className="flex h-8 items-center gap-1.5 rounded-full pl-3 pr-2 text-[13px] font-semibold transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
          style={{ background: HF.violetSoft, color: HF.brandText }}
        >
          {chip.label}
          <CloseIcon size={13} />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="ml-1 h-8 rounded-full px-2 text-[13px] font-semibold hover:underline"
        style={{ color: HF.text3 }}
      >
        Clear all
      </button>
    </div>
  );
}
